/**
 * The thread routes (contracts §9.1 slice 1, slice 1 spec §3.1) and their
 * query keys. `['threads']` is the list, `['thread', id]` one thread; the hub
 * events in `app/events.ts` invalidate both (C§10).
 */
import {
  MutationObserver,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { api, API_URL, ApiError, type Uploaded } from "../../app/api";
import { meKey, useMe, type Me } from "../../app/me";
import type { AreaRef } from "../areas/api";
import type { DocItem } from "../docs/api";
import type { TaskItem } from "../tasks/api";
import {
  newStart,
  newUnsent,
  notStarted,
  outbox,
  outboxMoves,
  pendingStart,
  refused,
  startOf,
  useStarts,
  useUnsent,
  type Unsent,
} from "./outbox";
import { placeholderDetail, withStarts } from "./starts";
import { editedBody } from "../files/composer-files";
import type { Handoff } from "../computer/api";
import type { Approval } from "../approvals/api";
import type { ThreadRoutine } from "../routines/api";

/** `handoff`: a handoff waits on someone in it (slice 6c §7.1), "Waiting on you". */
export type Activity = "running" | "queued" | "handoff" | null;

export type ThreadItem = {
  id: string;
  title: string;
  started_by_user_id: string | null;
  started_by: { id: string; name: string; initials: string; tint: string } | null;
  /** A person's iMessage thread (slice 2e spec §4.1), or one of their email chats (slice 2h §5.2). */
  messaging_app?: "imessage" | "email" | null;
  /** Its topic icon (spike S-324), null until it's picked: the tile shows its letter. */
  icon?: string | null;
  created_at: string;
  last_message_at: string;
  preview: string;
  reply_count: number;
  activity: Activity;
  /** Who the thread is with: the running turn's agent, else the latest turn's. */
  agent: { id: string; slug: string; name: string; glyph: string; tint: string } | null;
  /** Its Area's chip (slice 6d spec §4.4), null with none. Missing on an older Worker. */
  area?: AreaRef | null;
  area_id?: string | null;
  /** How it got its Area (slice 6d spec §7.1): `clef` shows "Filed by the crew". */
  area_filed_by?: "person" | "agent" | "clef" | "page" | "email" | null;
  /** Slice 4 §6.4: settled until a person writes again. Missing on an older Worker. */
  settled?: { at: string; by: string | null; via: "person" | "agent" | null } | null;
};

export type Thread = {
  id: string;
  title: string;
  started_by_user_id: string | null;
  /** A person's iMessage thread (slice 2e spec §4.1), or one of their email chats (slice 2h §5.2). */
  messaging_app?: "imessage" | "email" | null;
  /** Its topic icon (spike S-324), null until it's picked: the tile shows its letter. */
  icon?: string | null;
  created_at: string;
  last_message_at: string;
  /** Its Area (slice 6d spec §8.3), null with none; a messaging thread never has one. */
  area?: AreaRef | null;
  area_id?: string | null;
  /** How it got its Area (slice 6d spec §7.1): `clef` shows "Filed by the crew". */
  area_filed_by?: "person" | "agent" | "clef" | "page" | "email" | null;
  /** Slice 4 §6.4: settled until a person writes again. Missing on an older Worker. */
  settled?: { at: string; by: string | null; via: "person" | "agent" | null } | null;
};

export type MessageKind =
  | "text"
  | "task_card"
  | "line_items"
  | "system"
  | "doc_card"
  /** Slice 6c §3: the agent asks a person to take over the computer (`data: { handoff_id }`). */
  | "handoff_card"
  /** Slice 3 §8: a held write or routine, waiting on its approver (`data: { approval_id }`). */
  | "approval_card"
  /** S-525: the agent's question with tappable answers (`data`: `ChoiceData`). */
  | "choice_card"
  /** Slice 6i: an app the agent published (`data: { app_id, version }`). */
  | "app_card";

/** A `choice_card`'s data (S-525), as the Worker writes it. */
export type { ChoiceData } from "../../../worker/repo/types";

export type MessageAuthor = { id: string; name: string; initials: string; tint: string };

export type ThreadMessage = {
  id: string;
  thread_id: string;
  author_user_id: string | null;
  author_agent_id: string | null;
  /**
   * A person's message on `GET /api/threads/:id`: who wrote it, removed or
   * not. Null on an agent's; missing on a send's answer and an older Worker.
   */
  author?: MessageAuthor | null;
  kind: MessageKind;
  body: string;
  data: Record<string, unknown>;
  turn_id: string | null;
  part: number | null;
  /** The last streamed block appended to this part (C§6.5). */
  stream_seq: number | null;
  created_at: string;
};

export type TurnStatus = "queued" | "running" | "done" | "failed";

export type Turn = {
  id: string;
  thread_id: string;
  agent_id: string;
  status: TurnStatus;
  fail_reason: "couldnt_start" | "stopped" | "interrupted" | "ai_auth" | null;
  progress: { text: string; seq: number; at: string; frame?: ProgressFrame } | null;
  /** Slice 6c §4: the line is the agent using the computer's browser, so Watch shows. */
  progress_on_computer?: boolean;
  trigger_message_id: string | null;
  deferred_at: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  /**
   * Slice 1c §5.4: the model that answered, and the default when the turn
   * was claimed; `fallback` names the model when a fallback answered.
   */
  model?: string | null;
  routed_from?: string | null;
  fallback?: { model: string; label: string } | null;
  /** The turn's last reply part, on whichever page: the fallback line goes under it. */
  last_reply_id?: string | null;
  /** Slice 1d §4.5: the Memory files the turn changed, e.g. `["MEMORY.md"]`. */
  updated_files?: string[];
  /**
   * Slice 2a §5: the turn's successful web lookups, and whether one was
   * refused for the day's cap. Missing on older turns: read as none.
   */
  tools?: TurnTools;
  can_retry: boolean;
  /** Slice 1i: a person pressed Stop and its run is being stopped. Missing on an older Worker. */
  stop_requested_at?: string | null;
  /** Slice 5 §11.4: a routine run's reply texted to its person. Missing on an older Worker. */
  texted_to?: { id: string; name: string } | null;
  /** Slice 2h §8.3: a reply in an email chat, emailed to its person. Missing on an older Worker. */
  emailed_to?: { id: string; name: string } | null;
  /**
   * S-509: its fold line, "Worked for 37s · 9 steps · 1 failed", and the
   * hosts its browser visited. Missing on an older Worker; none until it ends.
   */
  work?: TurnWork;
};

export type TurnWork = { steps: number; failed: number; sites: string[] };

/** One of a turn's steps, as its fold opens them (`GET /api/threads/:id/turns/:turnId/steps`). */
export type TurnStep = {
  seq: number;
  call_id: string;
  tool: string;
  kind: string;
  status: "ok" | "error" | "unknown";
  error: string | null;
  params: string | null;
  result: string | null;
  details: string | null;
  duration_ms: number | null;
  detail: string;
  via?: string;
  /** Slice 6f: a browser step's page, and its picture (`frameUrl`) when `saved`. */
  url?: string | null;
  title?: string | null;
  frame?: "saved" | "sign_in" | null;
};

/**
 * The turn's latest picture of the browser (slice 6f §3.3), on its progress
 * line: live while it uses the browser, the moved-on row's after. `call_id`
 * is null for a sign-in page, which has none.
 */
export type ProgressFrame = {
  call_id: string | null;
  url: string | null;
  title: string | null;
  at: string;
  steps: number;
  ms: number;
  site: string | null;
  /** The newest step was on a sign-in page: no picture of it. */
  sign_in?: boolean;
};

/** A browser step's picture (slice 6f §3.4): `image/webp`, 404 once it's gone. */
export const frameUrl = (threadId: string, turnId: string, callId: string) =>
  `${API_URL}/api/threads/${threadId}/turns/${turnId}/frames/${encodeURIComponent(callId)}`;

export type TurnTools = { search_web: number; read_page: number; capped: boolean };

export type ThreadDetail = {
  thread: Thread;
  messages: ThreadMessage[];
  has_more: boolean;
  turns: Turn[];
  /** Every task opened in the thread (slice 2b spec §5): its cards render live from them. */
  tasks?: TaskItem[];
  /** The docs linked to the thread, archived too (slice 2d spec §8): its doc cards and chips. */
  docs?: DocItem[];
  /** Its handoffs (slice 6c §7.1): its handoff cards render live from them. Missing on an older Worker. */
  handoffs?: Handoff[];
  /** Its approvals (slice 3 §8): its approval cards render live from them. Missing on an older Worker. */
  approvals?: Approval[];
  /** S-525: the routines its routine lines name, for their Made rows. Missing on an older Worker. */
  routines?: ThreadRoutine[];
  /**
   * A messaging thread's link (slice 2e spec §6.3): whether its person's link
   * stands, and when a text last came in. Null on any other thread.
   */
  messaging?: ThreadMessaging | null;
};

export type ThreadMessaging = {
  /** Missing on an older Worker: iMessage. */
  app?: "imessage" | "email";
  linked: boolean;
  last_text_at: string | null;
};

export const threadsKey = ["threads"] as const;
export const threadKey = (id: string) => ["thread", id] as const;

export function threadsPath(
  tab: "everyone" | "mine",
  cursor: string | null,
  area: string | null = null,
): string {
  const q = new URLSearchParams();
  if (tab === "mine") q.set("mine", "1");
  if (area) q.set("area", area);
  if (cursor) q.set("cursor", cursor);
  const query = q.toString();
  return query ? `/api/threads?${query}` : "/api/threads";
}

type ThreadsPage = { threads: ThreadItem[]; next_cursor: string | null };

/**
 * The thread list, for Everyone or Mine (`?mine=1`, paged on its own), and
 * filtered to an Area's slug or `none` (slice 6d spec §4.4). All sit under
 * `['threads']`. A thread this tab is starting is on top until the list
 * holds it (`starts.ts`), only unfiltered: a start doesn't know its Area.
 */
export function useThreads(tab: "everyone" | "mine" = "everyone", area: string | null = null) {
  const starts = useStarts();
  const user = useMe().data?.user;
  const select = useCallback(
    (data: InfiniteData<ThreadsPage>) => (user && !area ? withStarts(data, starts, user) : data),
    [starts, user, area],
  );
  return useInfiniteQuery({
    queryKey: area ? [...threadsKey, tab, area] : [...threadsKey, tab],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => api<ThreadsPage>("GET", threadsPath(tab, pageParam, area)),
    getNextPageParam: (page) => page.next_cursor,
    select,
  });
}

/**
 * One thread. Hub events keep it fresh, so headers and titles reuse the cache
 * for a few seconds; the view that shows its messages passes
 * `refetchOnMount: "always"` (see `useThreadProjection`). While this tab is
 * starting it, the thread doesn't exist yet: no fetch (a 404), and its
 * placeholder (`isPlaceholderData`) until `POST /api/threads` lands.
 */
export function useThread(id: string, opts: { refetchOnMount?: "always" } = {}) {
  const start = pendingStart(useUnsent(id));
  // A start's words and time never change: one placeholder for it, through Not sent and Retry.
  const startId = start?.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const placeholder = useMemo(() => start && placeholderDetail(start), [startId]);
  return useQuery({
    queryKey: threadKey(id),
    queryFn: () => api<ThreadDetail>("GET", `/api/threads/${id}`),
    staleTime: 5_000,
    enabled: !start,
    placeholderData: placeholder,
    ...opts,
  });
}

/**
 * A finished turn's steps for its fold (S-509), fetched when it opens. They
 * never change once the turn has ended, so they're kept.
 */
export function useTurnSteps(threadId: string, turnId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...threadKey(threadId), "steps", turnId],
    queryFn: () =>
      api<{ steps: TurnStep[] }>("GET", `/api/threads/${threadId}/turns/${turnId}/steps`),
    enabled,
    staleTime: Infinity,
    select: (d) => d.steps,
  });
}

/**
 * Messages older than the loaded window (`GET /api/threads/:id?before=`), a
 * page at a time, oldest first. They're settled history: nothing streams there.
 */
export function useEarlierMessages(threadId: string) {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const load = useMutation({
    mutationFn: (before: string) =>
      api<ThreadDetail>("GET", `/api/threads/${threadId}?before=${encodeURIComponent(before)}`),
    onSuccess: (page) => {
      setMessages((loaded) => [...page.messages, ...loaded]);
      setHasMore(page.has_more);
    },
  });
  return { messages, hasMore, load };
}

type Asked = { thread: Thread; message: ThreadMessage; turn: Turn };

/** What a person sends: the text, a doc attached to it (slice 2d spec §11.4), and files (5b). */
export type Ask = { body: string; doc_id?: string; files?: Uploaded[] };

/** The files an unsent message carries, as the API takes them. */
const fileIdsOf = (u: Unsent) => (u.files?.length ? { file_ids: u.files.map((f) => f.id) } : {});

/** Puts a new message and its turn into the cached thread, so it shows before the refetch. */
function addAsked(client: QueryClient, threadId: string, message: ThreadMessage, turn: Turn) {
  client.setQueryData<ThreadDetail>(threadKey(threadId), (old) =>
    old && !old.messages.some((m) => m.id === message.id)
      ? { ...old, messages: [...old.messages, message], turns: [turn, ...old.turns] }
      : old,
  );
}

/**
 * An ask refused with `409 ai_not_connected` (slice 1b spec §4.5): the
 * connection changed since `/api/me` was read, so refetch it and the
 * composers show "Connect your AI to start".
 */
function onAskError(client: QueryClient) {
  return (error: Error) => {
    if (error instanceof ApiError && error.code === "ai_not_connected") {
      void client.invalidateQueries({ queryKey: meKey });
    }
  };
}

/** A send the API hasn't answered in this long is "Not sent": Retry is safe, it's the same id. */
const SEND_TIMEOUT_MS = 20_000;

/** The thread's sends go one at a time, in the order sent: its start first, then its replies. */
const sendScope = (threadId: string) => ({ id: `reply:${threadId}` });

/**
 * A new thread for the Chief of Staff, started optimistically: its id and its
 * first message's made here (an Edited start's again for the same words, so
 * one that may have landed is the same thread), shown from the outbox at
 * once, and sent. Returns the thread's id, to open it now.
 */
export function startThread(
  client: QueryClient,
  authorId: string,
  ask: { body: string; doc_id: string | null; area_id?: string | null; files?: Uploaded[] },
): string {
  const again = ask.files?.length ? null : outbox.restartIds(ask.body);
  const threadId = again?.thread_id ?? crypto.randomUUID();
  const start = newStart(threadId, authorId, ask, new Date(), again?.message_id);
  outbox.update(threadId, (l) => outboxMoves.add(l, start));
  sendStart(client, start);
  return threadId;
}

/**
 * `POST /api/threads` for a start in the outbox (`starts.ts`), with its pair:
 * a repeat (Retry) is the same thread, made once (contracts §9.1). In the
 * thread's send scope, so a reply typed before it lands waits behind it.
 */
function sendStart(client: QueryClient, start: Unsent) {
  const threadId = start.thread_id;
  const observer = new MutationObserver(client, {
    scope: sendScope(threadId),
    networkMode: "always",
    mutationFn: (u: Unsent) =>
      api<Asked>(
        "POST",
        "/api/threads",
        {
          body: u.body,
          thread_id: u.thread_id,
          message_id: u.id,
          ...(u.doc_id ? { doc_id: u.doc_id } : {}),
          ...(u.area_id ? { area_id: u.area_id } : {}),
          ...fileIdsOf(u),
        },
        { timeoutMs: SEND_TIMEOUT_MS },
      ),
    onSuccess: ({ thread, message, turn }: Asked, u: Unsent) => {
      // The real thread in place of the placeholder, before the outbox lets the fetch go.
      client.setQueryData<ThreadDetail>(
        threadKey(threadId),
        (old) => old ?? { thread, messages: [message], has_more: false, turns: [turn] },
      );
      // Its row into the lists until their refetch, where a refetch replaces it.
      const user = client.getQueryData<Me | null>(meKey)?.user;
      if (user) {
        client.setQueriesData<InfiniteData<ThreadsPage>>({ queryKey: threadsKey }, (old) =>
          old ? withStarts(old, [u], user) : old,
        );
      }
      // The cache holds it now: the outbox lets it go, whether or not a view is open.
      outbox.update(threadId, (l) => outboxMoves.drop(l, u.id));
      void client.invalidateQueries({ queryKey: threadKey(threadId) });
      void client.invalidateQueries({ queryKey: threadsKey });
    },
    onError: (error: Error, u: Unsent) => {
      onAskError(client)(error);
      outbox.update(threadId, (l) => outboxMoves.failed(l, u.id, error));
    },
  });
  observer.mutate(start).catch(() => undefined);
}

/**
 * `POST /api/threads/:id/messages`: a reply, sent optimistically. `send`
 * shows it at once from the outbox ("Sending…") with an id made here;
 * `retry` sends a "Not sent" one again with the same id (idempotent,
 * contracts §9.1); `edit` takes one back out, returning its text. Sends in a
 * thread go one at a time (a mutation scope), so they land in the order sent.
 *
 * Behind a start this tab is sending (`starts.ts`), a reply waits for it; if
 * the start isn't sent, neither is the reply (`not_started`), and Retry on
 * either sends the start, then them. `editStart` takes the start back with
 * every reply behind it: the thread never existed.
 */
export function usePostMessage(threadId: string, authorId: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    scope: sendScope(threadId),
    // Offline, fail at once (and show "Not sent") rather than wait paused.
    networkMode: "always",
    mutationFn: (u: Unsent) => {
      // Its start went first in this scope: if it isn't sent, there's no thread to post to.
      if (pendingStart(outbox.get(threadId))) return Promise.reject(notStarted());
      return api<Omit<Asked, "thread">>(
        "POST",
        `/api/threads/${threadId}/messages`,
        { body: u.body, id: u.id, ...(u.doc_id ? { doc_id: u.doc_id } : {}), ...fileIdsOf(u) },
        { timeoutMs: SEND_TIMEOUT_MS },
      );
    },
    // A refetch already on its way may predate the message: let the one after it land instead.
    onMutate: () => client.cancelQueries({ queryKey: threadKey(threadId) }),
    onSuccess: ({ message, turn }, u) => {
      addAsked(client, threadId, message, turn);
      outbox.update(threadId, (l) => outboxMoves.sent(l, u.id, { message, turn }));
      void client.invalidateQueries({ queryKey: threadKey(threadId) });
      void client.invalidateQueries({ queryKey: threadsKey });
    },
    onError: (error, u) => {
      onAskError(client)(error);
      outbox.update(threadId, (l) => outboxMoves.failed(l, u.id, error));
    },
  });
  const { mutate } = mutation;
  return {
    send: (ask: Ask) => {
      // Files go only with a new id (Devin on #435): an Edited reply's id would
      // bring back the files it was sent with, not these.
      const again = ask.files?.length ? null : outbox.resendId(threadId, ask.body);
      const u = newUnsent(threadId, authorId, ask, new Date(), again ?? undefined);
      outbox.update(threadId, (l) => outboxMoves.add(l, u));
      mutate(u);
    },
    retry: (id: string) => {
      const list = outbox.get(threadId);
      const start = startOf(list);
      if (start?.state === "failed") {
        // The start first, then every reply waiting on it, in the order sent.
        const failed = list.filter((u) => u.state === "failed");
        outbox.update(threadId, (l) => failed.reduce((n, u) => outboxMoves.sending(n, u.id), l));
        sendStart(client, start);
        for (const u of failed) if (u !== start) mutate(u);
        return;
      }
      const u = outbox.find(threadId, id);
      if (!u || u.state !== "failed") return;
      outbox.update(threadId, (l) => outboxMoves.sending(l, id));
      mutate(u);
    },
    /**
     * A start that wasn't sent, taken back with the replies behind it, their
     * words after its own; null unless it's Not sent. One that may have
     * landed keeps its pair for the same words (`outbox.restartIds`).
     */
    editStart: (): { body: string; doc_id: string | null; files: Uploaded[] } | null => {
      const list = outbox.get(threadId);
      const start = startOf(list);
      if (!start || start.state !== "failed") return null;
      outbox.update(threadId, () => []);
      if (!refused(start.error)) {
        outbox.reclaimStart({ id: start.id, thread_id: threadId, body: start.body });
      }
      const all = [start, ...list.filter((u) => u !== start)];
      // Each one's files too, back as chips (Codex on #435).
      const files = all.flatMap((u) => u.files ?? []);
      const body = all
        .map((u) => editedBody(u.body, u.files ?? []))
        .filter(Boolean)
        .join("\n\n");
      return { body, doc_id: start.doc_id, files };
    },
    edit: (id: string): { body: string; files: Uploaded[] } | null => {
      const u = outbox.find(threadId, id);
      if (!u || u.state !== "failed") return null;
      outbox.update(threadId, (l) => outboxMoves.drop(l, id));
      // It may have landed: keep its id for the same words, and ask the thread.
      if (!refused(u.error)) {
        outbox.reclaim(threadId, { id: u.id, body: u.body });
        void client.invalidateQueries({ queryKey: threadKey(threadId) });
      }
      return { body: u.body, files: u.files ?? [] };
    },
  };
}

export function useRenameThread(threadId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (title: string) =>
      api<{ thread: Thread }>("PATCH", `/api/threads/${threadId}`, { title }),
    onSuccess: ({ thread }) => {
      client.setQueryData<ThreadDetail>(threadKey(threadId), (old) =>
        old ? { ...old, thread } : old,
      );
      void client.invalidateQueries({ queryKey: threadsKey });
    },
  });
}

/**
 * The Area sheet's move (slice 6d spec §8.3): one `PATCH`, which moves the
 * chat's open tasks that had its old Area with it. The thread, the lists,
 * every Area page and the sidebar's counts refresh; the hub's `thread` and
 * `task` events do the same for other tabs.
 */
export function useMoveThread(threadId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (areaId: string | null) =>
      api<{ thread: Thread }>("PATCH", `/api/threads/${threadId}`, { area_id: areaId }),
    onSuccess: ({ thread }) => {
      client.setQueryData<ThreadDetail>(threadKey(threadId), (old) =>
        old ? { ...old, thread: { ...old.thread, ...thread } } : old,
      );
      for (const queryKey of [
        threadKey(threadId),
        threadsKey,
        ["tasks"],
        ["task"],
        ["area"],
        meKey,
      ]) {
        void client.invalidateQueries({ queryKey });
      }
    },
  });
}

/**
 * `POST /api/turns/:id/take-back`: a queued follow-up leaves the thread.
 * The caller puts `body` back in the composer.
 */
export function useTakeBack(threadId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (turnId: string) =>
      api<{ body: string; doc_id: string | null; files?: Uploaded[] }>(
        "POST",
        `/api/turns/${turnId}/take-back`,
      ),
    onSuccess: (_taken, turnId) => {
      client.setQueryData<ThreadDetail>(threadKey(threadId), (old) => {
        if (!old) return old;
        const trigger = old.turns.find((t) => t.id === turnId)?.trigger_message_id;
        return {
          ...old,
          messages: old.messages.filter((m) => m.id !== trigger),
          turns: old.turns.filter((t) => t.id !== turnId),
        };
      });
      void client.invalidateQueries({ queryKey: threadKey(threadId) });
      void client.invalidateQueries({ queryKey: threadsKey });
    },
  });
}

/**
 * Stop (slice 1i spec): the turn being answered stops. Its line says
 * "Stopping…" at once; the thread's own update brings the person's line.
 */
export function useStopTurn(threadId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (turnId: string) => api<{ turn: Turn }>("POST", `/api/turns/${turnId}/stop`),
    onSuccess: ({ turn }) => {
      client.setQueryData<ThreadDetail>(threadKey(threadId), (old) =>
        old
          ? { ...old, turns: old.turns.map((t) => (t.id === turn.id ? { ...t, ...turn } : t)) }
          : old,
      );
    },
    onSettled: () => client.invalidateQueries({ queryKey: threadKey(threadId) }),
  });
}

export function useRetryTurn(threadId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (turnId: string) => api<{ turn: Turn }>("POST", `/api/turns/${turnId}/retry`),
    onError: onAskError(client),
    onSettled: () => client.invalidateQueries({ queryKey: threadKey(threadId) }),
  });
}

/**
 * S-525: a tap on one of a question's answers. The thread's own update
 * brings the answered card and the queued turn; `answered` means someone
 * got there first, and the refetch shows their answer.
 */
export function useAnswerChoice(threadId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      ...answer
    }: { messageId: string } & ({ option: number } | { text: string })) =>
      api<{ message: ThreadMessage; turn: Turn }>(
        "POST",
        `/api/threads/${threadId}/choices/${messageId}`,
        answer,
      ),
    onError: onAskError(client),
    onSettled: () => client.invalidateQueries({ queryKey: threadKey(threadId) }),
  });
}
